import { useEffect, useMemo, useRef } from 'react';

import { Box, CircularProgress, createFilterOptions, FilterOptionsState, Stack, Typography } from '@mui/material';

import { DisabledTooltipContent } from 'domains/campaigns/components/CustomCampaign/components/DisabledTooltipContent';
import { Send } from 'domains/campaigns/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import { SegmentFilterEngagementFields, SegmentFilterState, SegmentFilterV2 } from 'domains/segments/types';
import useSendById from 'hooks/queries/useSendById';
import useSends from 'hooks/queries/useSends';
import { SendJobStatus } from 'models/enums';

type SendDropdownOption = {
    label: string;
    value: number | string;
    isHeader?: boolean;
    isLoading?: boolean;
};

// Sentinel values for the non-selectable group-header rows injected into the options list when
// `curationSendIds` is supplied. Never collide with real send ids, which are numbers.
const CURATION_HEADER_VALUE = '__curation_sends_header__';
const OTHER_SENDS_HEADER_VALUE = '__other_sends_header__';

const defaultFilterOptions = createFilterOptions<SendDropdownOption>();

export type Props = {
    activeChannelEngagementFilter?: SegmentFilterV2;
    filter: SegmentFilterState<SegmentFilterEngagementFields>;
    index: number;
    sends: Send[];
    /**
     * Ids of sends belonging to the curation currently being built. When supplied (and non-empty),
     * the dropdown is split into a "This curation" section pinned above "All other sends" instead
     * of a single flat list.
     */
    curationSendIds?: Set<number>;
    /**
     * True while the account-wide send fetch backing "All other sends" is still in flight. Only
     * meaningful alongside `curationSendIds` — shows a spinner on that section's header instead of
     * silently popping the results in once they resolve.
     */
    isOtherSendsLoading?: boolean;
    sx?: {
        autocomplete: object;
    };
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterEngagementFields>,
        resetPreview?: boolean
    ) => void;
    disabled?: boolean;
    disabledTooltipContent?: React.ReactNode;
};

const SendAutocomplete = ({
    activeChannelEngagementFilter,
    filter,
    index,
    sends,
    curationSendIds,
    isOtherSendsLoading,
    sx,
    updateSegmentFilter,
    disabled,
    disabledTooltipContent,
}: Props) => {
    const { autocompleteInputValue, autocompleteValue, categorySpecificFields } = filter;

    const { data: sendsByChannel, isFetching: isSendsByChannelFetching } = useSends({
        path: activeChannelEngagementFilter?.sendsRef,
        reactQueryOptions: {
            enabled: Boolean(activeChannelEngagementFilter?.sendsRef),
        },
    });

    // "All other sends" is backed by whichever of two fetches is active for this filter: the
    // shared account-wide fetch the caller reports via `isOtherSendsLoading`, or (for
    // channel-enhanced engagement filters) this component's own per-channel `sendsByChannel`
    // fetch above. Either one being in flight means the section isn't done loading yet.
    const isOtherSendsSectionLoading =
        Boolean(isOtherSendsLoading) || (Boolean(activeChannelEngagementFilter?.sendsRef) && isSendsByChannelFetching);

    const sendIdValue = categorySpecificFields?.value;
    const sendIdNumber = typeof sendIdValue === 'number' ? sendIdValue : null;

    const updateSegmentFilterRef = useRef(updateSegmentFilter);
    const filterRef = useRef(filter);
    updateSegmentFilterRef.current = updateSegmentFilter;
    filterRef.current = filter;

    // Check if send exists in available arrays
    const sendExistsInArray = useMemo(() => {
        if (!sendIdNumber) return false;
        const existsInSends = sends?.some((s) => s.id === sendIdNumber);
        const existsInSendsByChannel = sendsByChannel?.some((s) => s.id === sendIdNumber);
        return existsInSends || existsInSendsByChannel;
    }, [sendIdNumber, sends, sendsByChannel]);

    // Fetch send by ID if not in array
    const { data: fetchedSend, isSuccess: fetchedSendSuccess } = useSendById(sendIdNumber ?? 0, {
        enabled: Boolean(sendIdNumber) && !sendExistsInArray,
    });

    // Update autocomplete when fetched send is available.
    // `filter` and `updateSegmentFilter` are accessed via refs so that changing function/object
    // references from parent re-renders do not re-trigger this effect and cause an infinite loop.
    useEffect(() => {
        if (fetchedSend && fetchedSendSuccess && sendIdNumber === fetchedSend.id) {
            const latestFilter = filterRef.current;
            const currentValue = Array.isArray(latestFilter.autocompleteValue)
                ? latestFilter.autocompleteValue[0]
                : latestFilter.autocompleteValue;
            if (
                !currentValue ||
                currentValue.value !== fetchedSend.id ||
                latestFilter.autocompleteInputValue !== fetchedSend.name
            ) {
                updateSegmentFilterRef.current(index, {
                    ...latestFilter,
                    autocompleteValue: {
                        label: fetchedSend.name,
                        value: fetchedSend.id,
                    },
                    autocompleteInputValue: fetchedSend.name,
                });
            }
        }
    }, [fetchedSend, fetchedSendSuccess, sendIdNumber, index]);

    // Include fetched send in dropdown if not already present
    const sendsForDropdown = useMemo(() => {
        // The `sends` prop is the caller-supplied list to surface — e.g. the campaign's own sends,
        // which may be in-flight (negative sentinel ids) or drafts the channel query filters out.
        // Union them with the channel-fetched list rather than letting sendsByChannel replace them,
        // otherwise those caller sends appear and then vanish once sendsByChannel resolves.
        const channelSends = sendsByChannel ?? [];
        const propSends = sends ?? [];
        const channelIds = new Set(channelSends.map((s) => s.id));
        const baseSends = sendsByChannel
            ? [...propSends.filter((s) => !channelIds.has(s.id)), ...channelSends]
            : propSends;

        if (fetchedSend && fetchedSendSuccess && sendIdNumber === fetchedSend.id) {
            const alreadyExists = baseSends.some((s) => s.id === fetchedSend.id);
            if (!alreadyExists) {
                return [...baseSends, fetchedSend];
            }
        }
        return baseSends;
    }, [sendsByChannel, sends, fetchedSend, fetchedSendSuccess, sendIdNumber]);

    const sendDropdownOptions: SendDropdownOption[] = useMemo(() => {
        const filtered =
            sendsForDropdown?.filter(
                (c) => c.status !== 'FAILED' && c.status !== SendJobStatus.CANCELED && c.name?.trim()
            ) ?? [];
        const options = filtered.map((c) => ({ label: c.name, value: c.id }));

        if (!curationSendIds?.size) return options;

        const curationOptions = options.filter((o) => curationSendIds.has(o.value));
        const otherOptions = options.filter((o) => !curationSendIds.has(o.value));

        const otherSendsHeader: SendDropdownOption = {
            label: 'All other sends',
            value: OTHER_SENDS_HEADER_VALUE,
            isHeader: true,
            // `isOtherSendsSectionLoading` is sourced from state shared across every engagement
            // filter in the tree, so it can still read `true` here even after this instance's own
            // "other sends" have already resolved (e.g. while a sibling filter's fetch is in
            // flight). Only show the spinner while there's actually nothing to show yet.
            isLoading: isOtherSendsSectionLoading && otherOptions.length === 0,
        };

        return [
            { label: 'This curation', value: CURATION_HEADER_VALUE, isHeader: true },
            ...curationOptions,
            otherSendsHeader,
            ...otherOptions,
        ];
    }, [sendsForDropdown, curationSendIds, isOtherSendsSectionLoading]);

    // Re-groups the default text-filtered results back under whichever header(s) still have a
    // match, rather than MUI's default flat filtering — which would otherwise strand a header
    // above zero matching options, or drop "All other sends" (and its loading spinner) as soon as
    // it has no options yet.
    const filterSendOptions = (
        options: SendDropdownOption[],
        state: FilterOptionsState<SendDropdownOption>
    ): SendDropdownOption[] => {
        if (!curationSendIds?.size) return defaultFilterOptions(options, state);

        const curationHeader = options.find((o) => o.value === CURATION_HEADER_VALUE);
        const otherHeader = options.find((o) => o.value === OTHER_SENDS_HEADER_VALUE);
        const curationOptions = options.filter((o) => !o.isHeader && curationSendIds.has(o.value as number));
        const otherOptions = options.filter((o) => !o.isHeader && !curationSendIds.has(o.value as number));

        const matchedCuration = defaultFilterOptions(curationOptions, state);
        const matchedOther = defaultFilterOptions(otherOptions, state);

        const showCurationSection = matchedCuration.length > 0;
        const showOtherSection = matchedOther.length > 0 || Boolean(otherHeader?.isLoading);

        return [
            ...(showCurationSection && curationHeader ? [curationHeader, ...matchedCuration] : []),
            ...(showOtherSection && otherHeader ? [otherHeader, ...matchedOther] : []),
        ];
    };

    const handleInputValueChange = (newInputValue: string) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteInputValue: newInputValue,
        });
    };

    const handleValueChange = (value: any) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteValue: value,
            categorySpecificFields: {
                ...categorySpecificFields,
                value: value?.value,
                sendInteractionField: {
                    autocompleteInputValue: '',
                    autocompleteValue: null,
                    value: null,
                },
            },
        });
    };

    const sharedProps = {
        inputValue: autocompleteInputValue ?? '',
        isOptionEqualToValue: (option: { value: number }, value: { value: number }) => {
            const optionValue = typeof option.value === 'number' ? option.value : Number(option.value);
            const valueValue = typeof value.value === 'number' ? value.value : Number(value.value);
            return optionValue === valueValue;
        },
        getOptionDisabled: (option: SendDropdownOption) => Boolean(option.isHeader),
        filterOptions: filterSendOptions,
        renderOption: ({ className, ...optionProps }: any, option: SendDropdownOption) =>
            option.isHeader ? (
                <Box
                    component="li"
                    className={className}
                    {...optionProps}
                    key={option.value}
                    data-testid={`send-group-header-${option.value}`}
                    sx={{
                        backgroundColor: 'grey.100',
                        cursor: 'default',
                        // MUI dims disabled options via opacity; headers are disabled (non-selectable)
                        // but should stay fully legible, not faded like a real unavailable option.
                        opacity: 1,
                    }}
                >
                    {option.isLoading ? (
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <CircularProgress size={12} role="progressbar" aria-label="Loading other sends" />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, letterSpacing: 'normal' }}
                            >
                                Loading other sends…
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.03em' }}
                        >
                            {option.label}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box
                    component="li"
                    className={className}
                    {...optionProps}
                    key={option.value}
                    data-testid={`opt-${option.value}`}
                >
                    <Typography variant="body2">{option.label}</Typography>
                </Box>
            ),
        onChange: (_e: any, value: any) => handleValueChange(value),
        onInputChange: (_e: any, newInputValue: string) => handleInputValueChange(newInputValue),
        options: sendDropdownOptions ?? [],
        sx: { width: '33rem', ...sx?.autocomplete },
    };

    // TODO: uncomment when SendAutocomplete supports multi-select
    // if (user can select multiple sends) {
    //     return (
    //         <Stack direction="row" order="3">
    //             <Autocomplete
    //                 data-testid="multi-send-autocomplete"
    //                 label="Sends"
    //                 multiple
    //                 placeholder={
    //                     autocompleteValue && Array.isArray(autocompleteValue) && !!autocompleteValue?.length
    //                         ? ''
    //                         : 'Enter send names'
    //                 }
    //                 renderTags={(value, getTagProps) =>
    //                     value?.map((option, index) => (
    //                         <Chip
    //                             deleteIcon={<Close />}
    //                             label={option.label}
    //                             {...getTagProps({ index })}
    //                             sx={greyChipStyles}
    //                         />
    //                     ))
    //                 }
    //                 type="string"
    //                 value={autocompleteValue ?? []}
    //                 {...sharedProps}
    //             />
    //         </Stack>
    //     );
    // }

    return (
        <Stack direction="row" order="2">
            <DisabledTooltipContent disabled={!!disabled}>
                <span>
                    <Autocomplete
                        data-testid="send-autocomplete"
                        data-test="send-autocomplete"
                        disabled={disabled}
                        label="Send"
                        placeholder="Enter a send name"
                        type="string"
                        value={autocompleteValue ?? null}
                        {...sharedProps}
                    />
                </span>
            </DisabledTooltipContent>
        </Stack>
    );
};

export default SendAutocomplete;
